import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/api';
import type { MonsterCatalogEntry } from '@/lib/db/dungeon-types';

interface EntityPickerModalProps {
  onSelectMonster: (monster: MonsterCatalogEntry) => void;
  onSelectCustom: (type: 'npc' | 'object', name: string, icon: string) => void;
  onClose: () => void;
}

type Tab = 'catalog' | 'custom';

export function EntityPickerModal({
  onSelectMonster,
  onSelectCustom,
  onClose,
}: EntityPickerModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('catalog');

  // Monster Catalog state
  const [monsters, setMonsters] = useState<MonsterCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Custom entity state
  const [customType, setCustomType] = useState<'npc' | 'object'>('npc');
  const [customName, setCustomName] = useState('');
  const [customIcon, setCustomIcon] = useState('');

  useEffect(() => {
    apiFetch('/api/admin/monsters')
      .then((res) => res.json())
      .then((data: MonsterCatalogEntry[]) => setMonsters(data))
      .catch(() => setMonsters([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredMonsters = monsters.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      m.name.toLowerCase().includes(q) ||
      m.tags.some((t) => t.toLowerCase().includes(q))
    );
  });

  const handlePlaceCustom = () => {
    if (!customName.trim()) return;
    onSelectCustom(customType, customName.trim(), customIcon.trim() || '🗿');
  };

  const labelClass = 'block text-xs text-gray-500 uppercase tracking-wide mb-1';
  const inputClass =
    'w-full px-2 py-1.5 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-blue-500';

  return (
    /* Overlay */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal */}
      <div className="flex flex-col bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-lg max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 shrink-0">
          <h2 className="text-sm font-semibold text-gray-100">Place Entity</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 shrink-0">
          {(
            [
              { id: 'catalog', label: 'Monster Catalog' },
              { id: 'custom', label: 'Custom NPC / Object' },
            ] as { id: Tab; label: string }[]
          ).map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'flex-1 py-2 text-xs font-medium transition-colors',
                activeTab === id
                  ? 'text-blue-400 border-b-2 border-blue-500 bg-gray-800/40'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/20'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {activeTab === 'catalog' ? (
            <>
              {/* Search */}
              <div className="px-4 py-3 border-b border-gray-700 shrink-0">
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    placeholder="Search by name or tag…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Monster list */}
              <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
                {loading ? (
                  <p className="text-xs text-gray-500 text-center py-6">Loading…</p>
                ) : filteredMonsters.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-6">No monsters found.</p>
                ) : (
                  filteredMonsters.map((monster) => (
                    <button
                      key={monster.id}
                      onClick={() => onSelectMonster(monster)}
                      className="w-full flex items-start gap-3 px-3 py-2.5 rounded text-left hover:bg-gray-800 transition-colors group"
                    >
                      {/* Icon */}
                      <span
                        className="text-xl leading-none mt-0.5 shrink-0"
                        aria-hidden="true"
                      >
                        {monster.icon}
                      </span>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-sm font-medium text-gray-100 group-hover:text-white truncate">
                            {monster.name}
                          </span>
                          <span className="text-xs text-gray-500 shrink-0">
                            HP {monster.hp}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 line-clamp-1">
                          {monster.description}
                        </p>
                      </div>

                      {/* Difficulty */}
                      <div className="shrink-0 flex items-center gap-0.5 mt-0.5" title="Difficulty">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span
                            key={i}
                            className={cn(
                              'w-1.5 h-1.5 rounded-full',
                              i < monster.difficulty ? 'bg-orange-500' : 'bg-gray-700'
                            )}
                          />
                        ))}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            /* Custom NPC / Object tab */
            <div className="px-4 py-4 space-y-4">
              <div>
                <label className={labelClass}>Type</label>
                <select
                  className={inputClass}
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value as 'npc' | 'object')}
                >
                  <option value="npc">NPC</option>
                  <option value="object">Object</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Name</label>
                <input
                  type="text"
                  className={inputClass}
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Enter a name…"
                />
              </div>

              <div>
                <label className={labelClass}>Icon</label>
                <input
                  type="text"
                  className={inputClass}
                  value={customIcon}
                  onChange={(e) => setCustomIcon(e.target.value)}
                  placeholder="🗿"
                  maxLength={2}
                />
              </div>

              <button
                onClick={handlePlaceCustom}
                disabled={!customName.trim()}
                className={cn(
                  'w-full py-2 rounded text-sm font-medium transition-colors',
                  customName.trim()
                    ? 'bg-blue-600 text-white hover:bg-blue-500'
                    : 'bg-gray-800 text-gray-600 cursor-not-allowed'
                )}
              >
                Place Entity
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
